import React from "react";
import { Grid } from "Components/Base/Layout";
import { Box } from "Components/Base/Layout/Box";
import { Typography } from "Components/Base/DataDisplay";
import { Paper } from "Components/Base/Surfaces";
import { Accordion } from "Components/Base/Surfaces/Accordion";
import ButtonUnderlined from "Components/Fragment/ButtonUnderlined";
import ActiveFiltersBar from "./ActiveFiltersBar";
import DateFilters from "./Filter/DateFilters";
import SessionFilters from "./Filter/SessionFilters";
import CategoryFilters from "./Filter/CategoryFilters";
import FilterMobileSummary from "./Filter/FilterMobileSummary";
import FilterMobileDetails from "./Filter/FilterMobileDetails";
import { useFilter } from "../hooks";
import { useLearningCatalogue } from "../context";
import { STYLE_PAPER, STYLE_FILTER_BOX } from "../styles";

const Filter = ({ languageContext, isMobile = false, filterContainerRef }) => {
  const {
    otherCategories,
    categoriesValue,
    startDate,
    endDate,
    sessionRegion,
    sessionCity,
    hasActiveFilters,
    hitCount,
    uniqueRegions,
    uniqueCities,
    handleChange,
    handleDateChange,
    handleSessionChange,
    resetFilters,
  } = useFilter();

  const { handleShowAdvancedFilter, reloadFromEndpoint } =
    useLearningCatalogue();
  const totalFilterGroupsCount =
    (otherCategories?.categories?.length || 0) +
    (uniqueRegions?.length || 0) +
    (uniqueCities?.length || 0);

  if (isMobile) {
    return (
      <Accordion
        id="learningCatalogue-filter-mobile"
        summaryText={
          <FilterMobileSummary
            languageContext={languageContext}
            hasActiveFilters={hasActiveFilters}
            hitCount={hitCount}
          />
        }
        detailsText={
          <FilterMobileDetails
            languageContext={languageContext}
            hasActiveFilters={hasActiveFilters}
            resetFilters={resetFilters}
            startDate={startDate}
            endDate={endDate}
            handleDateChange={handleDateChange}
            uniqueRegions={uniqueRegions}
            uniqueCities={uniqueCities}
            sessionRegion={sessionRegion}
            sessionCity={sessionCity}
            handleSessionChange={handleSessionChange}
            otherCategories={otherCategories}
            categoriesValue={categoriesValue}
            handleChange={handleChange}
          />
        }
        defaultExpanded={hasActiveFilters}
        disableGutters={false}
        border={true}
        boxShadow={false}
        square
        slotProps={{
          root: {
            sx: {
              borderRadius: "2px !important",
            },
          },
          details: {
            sx: {
              padding: "8px 12px 12px !important",
            },
          },
        }}
      />
    );
  }

  return (
    <div ref={filterContainerRef} id="learningCatalogue-filters" tabIndex={-1}>
      <Paper elevation={0} square sx={STYLE_PAPER}>
        <Typography variant="h2" className="sr-only">
          {languageContext("catalogueFiltersGroup")}
        </Typography>
        <Box
          display="flex"
          alignItems="flex-end"
          justifyContent="flex-end"
          gap={1}
          sx={STYLE_FILTER_BOX}
        >
          <ButtonUnderlined
            label={languageContext("reload")}
            toolTipTitle={languageContext("reloadpage")}
            onClick={reloadFromEndpoint}
            size="small"
            disableFocusRipple={true}
          />
          <ButtonUnderlined
            label={languageContext("hideCatalogueFilters")}
            onClick={handleShowAdvancedFilter}
            size="small"
            disableFocusRipple={true}
          />
        </Box>
        {hasActiveFilters && (
          <ActiveFiltersBar
            languageContext={languageContext}
            hitCount={hitCount}
            onReset={resetFilters}
          />
        )}
        <br />
        <Grid container spacing={2}>
          <DateFilters
            languageContext={languageContext}
            startDate={startDate}
            endDate={endDate}
            onChange={handleDateChange}
            isMobile={false}
          />
          <SessionFilters
            languageContext={languageContext}
            uniqueRegions={uniqueRegions}
            uniqueCities={uniqueCities}
            sessionRegion={sessionRegion}
            sessionCity={sessionCity}
            onChange={handleSessionChange}
            isMobile={false}
            totalFilterGroupsCount={totalFilterGroupsCount}
          />
          <CategoryFilters
            languageContext={languageContext}
            categories={otherCategories?.categories}
            categoriesValue={categoriesValue}
            onChange={handleChange}
            isMobile={false}
            totalFilterGroupsCount={totalFilterGroupsCount}
          />
        </Grid>
      </Paper>
    </div>
  );
};

export default Filter;
